import { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { useAppDispatch, useAppSelector } from "@/hooks/useRedux";
import { getCookieStorage, setCookieStorage } from "@/utils/CookieStorage";
import { FreshchatService } from "@/services/freshchat.service";

import * as actions from "@/redux/AuthReducer";

const useAuthFlow = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const loginData = useAppSelector((state) => state.auth) || {};

  const status = loginData?.isLoggingIn;

  const [login, setLogin] = useState(false);
  const [token, setToken] = useState(null);
  const [showToast, setShowToast] = useState<any>(false);
  const [loginModal, setLoginModal] = useState(false);

  useEffect(() => {
    setShowToast(
      JSON.parse(
        localStorage.getItem("LOGIN_TOAST_DATA") || JSON.parse("false")
      )
    );
  }, []);

  useEffect(() => {
    if (token || (loginData?.data?.access && loginData?.data?.refresh)) {
      setCookieStorage(
        "__authAccessToken",
        loginData?.data?.access,
        30,
        "days"
      );
      setCookieStorage(
        "__authRefreshToken",
        loginData?.data?.refresh,
        90,
        "days"
      );
      if (loginData?.data?.primaryContact)
        setCookieStorage(
          "__primaryContact",
          loginData?.data?.primaryContact,
          90
        );
    }
  }, [status]);

  useEffect(() => {
    if (
      getCookieStorage("__authAccessToken") !== "" &&
      getCookieStorage("__authRefreshToken") !== ""
    ) {
      setToken(true);
      setLogin(true);
    } else {
      setLogin(false);
      setToken(false);
    }
  }, [status]);

  /**
   * Logout function with comprehensive cleanup.
   * 
   * This function handles:
   * 1. Freshchat widget cleanup (CRITICAL for preventing cross-user contamination)
   * 2. Redux state cleanup
   * 3. Cookie cleanup
   * 4. Navigation to home page
   * 
   * Why Freshchat cleanup is first:
   * - Must happen before Redux state is cleared
   * - Ensures widget is destroyed while we still know user context
   * - Prevents old user's chat data from leaking to next user
   * 
   * Flow:
   * 1. Check if user is on nutrition onboarding page
   * 2. If yes, aggressively cleanup Freshchat widget
   * 3. Clear Redux state (triggers auth reducer logout)
   * 4. Show success toast
   * 5. Redirect to home page
   */
  const logout = () => {
    console.log('999 :: LOGOUT :: Starting logout process');
    console.log('999 :: LOGOUT :: Current route:', router.pathname);
    
    // Step 1: Check if user is on nutrition onboarding page
    // If yes, cleanup Freshchat widget to prevent cross-user contamination
    const isOnNutritionPage = router.pathname.includes('/user-onboarding/current-onboarding-status');
    
    if (isOnNutritionPage) {
      console.log('999 :: LOGOUT :: User on nutrition page, triggering Freshchat cleanup');
      try {
        // Aggressive cleanup: Destroy widget, remove script, clear all static state
        FreshchatService.destroy();
        console.log('999 :: LOGOUT :: Freshchat cleanup completed');
      } catch (error) {
        console.error('999 :: LOGOUT :: Error during Freshchat cleanup:', error);
        // Continue with logout even if Freshchat cleanup fails
      }
    } else {
      console.log('999 :: LOGOUT :: User not on nutrition page, skipping Freshchat cleanup');
    }
    
    // Step 2: Clear local state
    setLogin(false);
    setToken(false);
    
    // Step 3: Dispatch Redux logout action
    // This clears Redux state and removes auth cookies
    console.log('999 :: LOGOUT :: Dispatching Redux logout action');
    dispatch(actions.logout());
    
    // Step 4: Show success toast
    setShowToast({
      type: "success",
      message: "Logout Successful",
    });
    localStorage.setItem(
      "LOGIN_TOAST_DATA",
      JSON.stringify({
        type: "success",
        message: "Logout Successful",
      })
    );
    
    // Step 5: Redirect to home page
    console.log('999 :: LOGOUT :: Redirecting to home page');
    router.push("/");
    
    console.log('999 :: LOGOUT :: Logout process completed');
  };

  return {
    login,
    token,
    setToken,
    loginModal,
    setLoginModal,
    showToast,
    setShowToast,
    logout,
  };
};

export default useAuthFlow;
